import { getPreSignedUrl, uploadSingleTaskFile, uploadToS3SingleTaskFile, FileMappingDto } from "api/task.api";
import { PortalUploadModeEnum } from "api/api.typing";
import { TaskFileUploadType } from "pages/project/common/taskBase.typing";
import { create } from "zustand";

function chunk<T>(array: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
}

export interface FileUploadStatus {
  file: File;
  progression: number;
  abortController?: AbortController;
  remainingTime?: string;
  status: "waiting" | "uploading" | "uploaded" | "failed" | "canceled";
}

interface FileUploadState {
  taskId: string | null;
  taskCode: string | null;
  fileStatuses: Map<string, FileUploadStatus>;
  fileUploaded: FileUploadStatus[];
  fileFailedOrCanceled: FileUploadStatus[];
  upload: (
    taskId: string,
    taskCode: string,
    isTaskDelivered: boolean,
    files: File[],
    fileType: TaskFileUploadType,
    onDeliverTask: (markAsDelivered: boolean, allFilesHaveBeenUploaded: boolean) => void,
    portalUploadMode: PortalUploadModeEnum,
    fileMappings?: Map<string, FileMappingDto>,
    comment?: string
  ) => void;
  cancel: (file: File) => void;
  cancelAll: () => void;
  reset: () => void;
}

export const MAX_PROGRESS_LIMIT_BEFORE_CANCEL = 90;
export const NBR_PARALLEL_UPLOAD = 3;

const getFilesByStatus = (filestatuses: Map<string, FileUploadStatus>) => {
  const statuses = [...filestatuses.values()];
  const fileUploaded = statuses.filter((x) => x.status === "uploaded");
  const fileFailedOrCanceled = statuses.filter((x) => x.status !== "uploaded");
  return [fileUploaded, fileFailedOrCanceled];
};

export const canCancelUpload = (fileUploadStatus: FileUploadStatus | undefined): boolean => {
  if (!fileUploadStatus) {
    return false;
  }
  const isWaiting = fileUploadStatus.status === "waiting";
  const isUploadingAndUnderTheLimit =
    fileUploadStatus.status === "uploading" && fileUploadStatus.progression < MAX_PROGRESS_LIMIT_BEFORE_CANCEL;
  return isWaiting || isUploadingAndUnderTheLimit;
};

export const canCancelAllUploads = (fileUploadStatus: Map<string, FileUploadStatus>): boolean => {
  if (!fileUploadStatus || fileUploadStatus.size === 0) {
    return false;
  }
  return [...fileUploadStatus.values()].some(canCancelUpload);
};

export const isFilesUploading = (fileUploadStatus: Map<string, FileUploadStatus>): boolean => {
  if (!fileUploadStatus || fileUploadStatus.size === 0) {
    return false;
  }
  return [...fileUploadStatus.values()].some(({ status }) => ["waiting", "uploading"].includes(status));
};

export const useFileUploadStore = create<FileUploadState>((setState, getState) => {
  function cancel(file: File) {
    const fileUploadStatus = getState().fileStatuses.get(file.name);
    if (!canCancelUpload(fileUploadStatus)) {
      return; // api limitation if it's almost uploaded we cannot cancel the request.
    }
    fileUploadStatus?.abortController?.abort(); // abort the request
    // mark the file as canceled
    setState((state) => {
      const { fileStatuses } = state;
      fileStatuses.set(file.name, { file: file, progression: 0, status: "canceled" });
      return { fileStatuses };
    });
  }

  function cancelAll() {
    const filesToCancel = [...getState().fileStatuses.values()]
      .filter((fs) => ["waiting", "uploading"].includes(fs.status))
      .map((fs) => fs.file);
    for (const file of filesToCancel) {
      getState().cancel(file);
    }
  }

  function reset() {
    setState({ taskId: null, taskCode: null, fileStatuses: new Map(), fileUploaded: [], fileFailedOrCanceled: [] });
  }

  function prepareUpload(file: File) {
    const onUploading = (abort: AbortController) =>
      setState((state) => {
        const { fileStatuses } = state;
        fileStatuses.set(file.name, { file, progression: 0, status: "uploading", abortController: abort });
        return { fileStatuses };
      });

    const onProgress = (progress: number, remainingTime?: string) =>
      setState((state) => {
        const { fileStatuses } = state;
        const value = fileStatuses.get(file.name) as FileUploadStatus;
        fileStatuses.set(file.name, { ...value, status: "uploading", progression: progress, remainingTime });
        return { fileStatuses };
      });

    const onUploaded = () =>
      setState((state) => {
        const { fileStatuses } = state;
        fileStatuses.set(file.name, { file, progression: 100, status: "uploaded" });
        return { fileStatuses };
      });

    const onFailed = (isCancellation: boolean) =>
      setState((state) => {
        const { fileStatuses } = state;
        fileStatuses.set(file.name, { file, progression: 0, status: isCancellation ? "canceled" : "failed" });
        return { fileStatuses };
      });
    return { onUploading, onProgress, onUploaded, onFailed };
  }

  function uploadOneFileToS3(url: string, file: File) {
    const status = getState().fileStatuses.get(file.name)?.status;
    if (status === "canceled") {
      return Promise.resolve();
    }
    const { onUploading, onProgress, onUploaded, onFailed } = prepareUpload(file);
    return uploadToS3SingleTaskFile({ presignedUrl: url, file }, onUploading, onProgress, onUploaded, onFailed);
  }

  function uploadOneFile(taskId: string, fileType: TaskFileUploadType, file: File, fileMapping?: FileMappingDto) {
    const status = getState().fileStatuses.get(file.name)?.status;
    if (status === "canceled") {
      return Promise.resolve();
    }
    const { onUploading, onProgress, onUploaded, onFailed } = prepareUpload(file);
    return uploadSingleTaskFile(taskId, file, fileType, onUploading, onProgress, onUploaded, onFailed, fileMapping);
  }

  async function upload(
    taskId: string,
    taskCode: string,
    isTaskDelivered: boolean,
    files: File[],
    fileType: TaskFileUploadType,
    onDeliverTask: (
      markAsDelivered: boolean,
      allFilesHaveBeenUploaded: boolean,
      comment?: string,
      remarks?: string,
      qaFormRatings?: Array<{
        key: number;
        value: number | null;
      }>,
      isNoteDelivery?: boolean
    ) => void,
    portalUploadMode: PortalUploadModeEnum,
    fileMappings?: Map<string, FileMappingDto>,
    comment?: string
  ) {
    // starting
    setState(() => {
      const fileStatuses = new Map();
      for (const file of files) {
        fileStatuses.set(file.name, { file, progression: 0, status: "waiting" });
      }
      return { fileStatuses, taskId, taskCode, fileUploaded: [], fileFailedOrCanceled: [] };
    });

    // call presigned url
    if (
      portalUploadMode === PortalUploadModeEnum.S3WithAntivirus ||
      portalUploadMode === PortalUploadModeEnum.GatewayService
    ) {
      // Convert Map to Record for API
      const mappingsRecord = fileMappings ? Object.fromEntries(fileMappings) : undefined;

      const presignedUrls = await getPreSignedUrl(
        taskId,
        files.map((f) => f.name),
        fileType,
        portalUploadMode,
        mappingsRecord
      );
      if (!presignedUrls) {
        setState((state) => {
          const { fileStatuses } = state;
          for (const file of files) {
            fileStatuses.set(file.name, { file, progression: 0, status: "failed" });
          }
          return { fileStatuses };
        });
        return;
      }

      const chunkedFiles = chunk(files, NBR_PARALLEL_UPLOAD);
      for (const someFiles of chunkedFiles) {
        await Promise.all(
          someFiles.map((file) => {
            const urlData = presignedUrls.find((url) => {
              return url.fileName === file.name;
            });
            if (!urlData) {
              setState((state) => {
                const { fileStatuses } = state;
                fileStatuses.set(file.name, { file, progression: 0, status: "failed" });
                return { fileStatuses };
              });
              return Promise.resolve();
            }
            return uploadOneFileToS3(urlData.uploadUrl, file);
          })
        );
      }
    } else {
      // old upload
      const chunkedFiles = chunk(files, NBR_PARALLEL_UPLOAD);
      for (const someFiles of chunkedFiles) {
        // uploading file by file
        await Promise.all(
          someFiles.map((f) => {
            const mapping = fileMappings?.get(f.name);
            return uploadOneFile(taskId, fileType, f, mapping);
          })
        );
      }
    }

    const fileStatuses = getState().fileStatuses;
    const [fileUploaded, fileFailedOrCanceled] = getFilesByStatus(fileStatuses);

    // upload is done
    setState({ fileUploaded, fileFailedOrCanceled });

    // only if any file isUploaded
    // we can call api to notify the PM about new delivery
    if (fileUploaded.length > 0) {
      const allFilesHaveBeenUploaded = fileFailedOrCanceled.length === 0;
      await onDeliverTask(
        isTaskDelivered,
        allFilesHaveBeenUploaded,
        undefined,
        comment,
        undefined,
        fileType === "notes"
      );
    }
  }

  return {
    taskId: null,
    taskCode: null,
    fileStatuses: new Map(),
    fileUploaded: [],
    fileFailedOrCanceled: [],
    upload,
    cancel,
    cancelAll,
    reset,
  };
});
